import { useState, useEffect } from 'react';
import { Button, Space, Modal } from 'antd';
import { EditOutlined, ExpandOutlined } from '@ant-design/icons';
import { Trans } from '@lingui/macro';
import JsonView from '@uiw/react-json-view';

import JsonInput from './JsonInput';
import { JsonSaveCancelButtons, JsonCopyButton } from './JsonActionButtons';
import { useJsonOperations } from '@hooks';

import styles from './JsonViewEdit.module.scss';

type JsonViewEditProps = {
  value: Record<string, unknown> | null;
  onChange: (value: Record<string, unknown>) => void;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  className?: string;
  label?: React.ReactNode;
  disabled?: boolean;
  expandAllKeys?: boolean;
};

const JsonViewEdit = ({
  value,
  onChange,
  placeholder,
  minRows = 5,
  maxRows = 20,
  className,
  label,
  disabled = false,
  expandAllKeys = false,
}: JsonViewEditProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isEditingInModal, setIsEditingInModal] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const { isSaving, handleSaveWithLoading } = useJsonOperations();

  const handleSave = async (newValue: Record<string, unknown>) => {
    setLocalValue(newValue);
    await handleSaveWithLoading(
      () => onChange(newValue),
      () => {
        setIsEditing(false);
        setIsEditingInModal(false);
      }
    );
  };

  return (
    <div className={className}>
      {isEditing ? (
        <JsonInput
          value={value}
          onChange={handleSave}
          placeholder={placeholder}
          minRows={minRows}
          maxRows={maxRows}
          showButtonsInline={false}
          onCancel={() => setIsEditing(false)}
          renderButtons={({ hasChanges, error, onSave, onCancel }) => (
            <JsonSaveCancelButtons
              hasChanges={hasChanges}
              error={error}
              onSave={onSave}
              onCancel={onCancel}
              isSaving={isSaving}
              className={styles.editHeader}
            />
          )}
        />
      ) : (
        <>
          <div className={styles.header}>
            {label && <span className={styles.label}>{label}</span>}
            <Space size="small">
              <JsonCopyButton value={localValue} />
              {!disabled && (
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setIsEditing(true)}
                >
                  <Trans>Edit</Trans>
                </Button>
              )}
              <Button
                type="text"
                size="small"
                icon={<ExpandOutlined />}
                onClick={() => setIsFullscreen(true)}
              >
                <Trans>Expand</Trans>
              </Button>
            </Space>
          </div>
          <div className={styles.jsonViewContainer}>
            <JsonView
              value={localValue || {}}
              collapsed={expandAllKeys ? false : 1}
              displayDataTypes={false}
              style={
                {
                  '--w-rjv-font-family': 'Monaco, Menlo, Ubuntu Mono, Consolas, source-code-pro, monospace',
                  '--w-rjv-line-height': '1.8',
                  '--w-rjv-font-size': '1.3rem',
                  '--w-rjv-background-color': 'transparent',
                  '--w-rjv-border-left': '1px solid #d9d9d9',
                } as any
              }
            />
          </div>
        </>
      )}

      <Modal
        title={
          !isEditingInModal ? (
            <div className={styles.modalHeader}>
              <span><Trans>JSON Data</Trans></span>
              <Space size="small">
                <JsonCopyButton value={localValue} />
                {!disabled && (
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditingInModal(true)}
                  >
                    <Trans>Edit</Trans>
                  </Button>
                )}
              </Space>
            </div>
          ) : (
            <span><Trans>JSON Data - Edit Mode</Trans></span>
          )
        }
        open={isFullscreen}
        onCancel={() => {
          setIsFullscreen(false);
          setIsEditingInModal(false);
        }}
        footer={null}
        width="50%"
        className={styles.fullscreenModal}
        centered
      >
        {isEditingInModal ? (
          <JsonInput
            value={value}
            onChange={handleSave}
            placeholder={placeholder}
            minRows={20}
            maxRows={50}
            showButtonsInline={false}
            onCancel={() => setIsEditingInModal(false)}
            renderButtons={({ hasChanges, error, onSave, onCancel }) => (
              <JsonSaveCancelButtons
                hasChanges={hasChanges}
                error={error}
                onSave={onSave}
                onCancel={onCancel}
                isSaving={isSaving}
                className={styles.modalEditHeader}
              />
            )}
          />
        ) : (
          <div className={styles.fullscreenJsonContainer}>
            <JsonView
              value={localValue || {}}
              collapsed={false}
              displayDataTypes={false}
              style={
                {
                  '--w-rjv-font-family': 'Monaco, Menlo, Ubuntu Mono, Consolas, source-code-pro, monospace',
                  '--w-rjv-line-height': '1.8',
                  '--w-rjv-font-size': '1.3rem',
                  '--w-rjv-background-color': 'transparent',
                  '--w-rjv-border-left': '1px solid #d9d9d9',
                } as any
              }
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default JsonViewEdit;
